import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../../api/client";

export const threadListQueryOptions = {
  queryKey: ["direct-messages", "threads"],
  queryFn: () => apiFetch("/direct-messages/threads"),
};

export const unreadCountQueryOptions = {
  queryKey: ["direct-messages", "unread-count"],
  queryFn: () => apiFetch("/direct-messages/unread-count"),
};

export const useThreadList = (options = {}) =>
  useQuery({
    ...threadListQueryOptions,
    select: (data) => data.threads || [],
    ...options,
  });

// Messages and the unread badge need only the token, not the profile. Called
// from AuthProvider so they load alongside /auth/me instead of after the shell.
export const usePrefetchInbox = (enabled) => {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!enabled) return;
    queryClient.prefetchQuery(threadListQueryOptions);
    queryClient.prefetchQuery(unreadCountQueryOptions);
  }, [enabled, queryClient]);
};

export const useThreadMessages = (contactRequestId, options = {}) =>
  useQuery({
    queryKey: ["direct-messages", "thread", contactRequestId],
    queryFn: () => apiFetch(`/direct-messages/thread/${contactRequestId}`),
    select: (data) => data.messages || [],
    enabled: Boolean(contactRequestId) && (options.enabled ?? true),
    ...options,
  });

export const useSendMessage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ contactRequestId, body }) =>
      apiFetch(`/direct-messages/thread/${contactRequestId}`, {
        method: "POST",
        body: { body },
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["direct-messages", "thread", variables.contactRequestId],
      });
      queryClient.invalidateQueries({ queryKey: ["direct-messages", "threads"] });
      queryClient.invalidateQueries({ queryKey: ["direct-messages", "unread-count"] });
    },
  });
};

export const useMarkThreadRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ contactRequestId }) =>
      apiFetch(`/direct-messages/thread/${contactRequestId}/read`, { method: "POST" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["direct-messages", "unread-count"] });
    },
  });
};

export const useUnreadMessageCount = (options = {}) =>
  useQuery({
    ...unreadCountQueryOptions,
    select: (data) => data?.count ?? 0,
    refetchInterval: 60_000,
    ...options,
  });
